<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { type SelectOption } from 'naive-ui';
import {
  createFileReviews,
  searchFileReview,
  updateReview,
  type FileReviewCreateUser,
  type ReviewInDb,
  type ReviewUpdateUser,
  type VaultFileRevisionInDb,
  type VaultFileRevisionSnapshot,
} from '@/api';
import CkEditorInput from '@/components/CkeditorInput.vue';
import FileSelector from '@/components/FileSelector.vue';
import ReviewCommentSection from './ReviewCommentSection.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaListItem from '@/components/design/composite/GaiaListItem.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { AGENT_EMAIL, AGENT_LABEL } from '@/composables/review/useReviewSearchFilters';
import { useAuthStore } from '@/stores/auth';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useKanbanStore } from '@/stores/kanbanStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useVaultStore } from '@/stores/vaultStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Everything about a review that isn't its extractions: due date, description,
 * people, state, the files attached to it, and its comment thread.
 *
 * This is the board's old ticket modal, moved into a drawer beside the list.
 * The card body navigates to the review itself, so these edits needed somewhere
 * else to live — and nothing here exists on the detail views: this drawer is the
 * only place in the app that can set a review's due date or read its comments.
 */

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  review: {
    type: Object as PropType<ReviewInDb | null>,
    default: null,
  },
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  /** A field was written; the list refetches so its rows agree with the drawer. */
  saved: [review: ReviewInDb];
}>();

const authStore = useAuthStore();
const gaiaStore = useGaiaStore();
const kanbanStore = useKanbanStore();
const organizationStore = useOrganizationStore();
const vaultStore = useVaultStore();
const message = useGaiaToast();

const description = ref<string>('');
const dueDate = ref<number | null>(null);
const assignee = ref<string | null>(null);
const reporter = ref<string | null>(null);
const stateUid = ref<string | null>(null);

const isSaving = ref<boolean>(false);
const savingError = ref<string | null>(null);

interface AttachedFile {
  file: VaultFileRevisionSnapshot;
  revision: VaultFileRevisionInDb | null;
}

const attachedFiles = ref<AttachedFile[]>([]);
const showAddFileModal = ref<boolean>(false);
const processingAddFile = ref<boolean>(false);
const addFileSelectedKeys = ref<string[]>([]);

const canEdit = computed(() => authStore.userPermissions.can_update_reviews);

const stateOptions = computed<SelectOption[]>(() =>
  kanbanStore.sortedKanbanStates.map(state => ({ label: state.name, value: state.uid }))
);

const userOptions = computed<SelectOption[]>(() => [
  { label: AGENT_LABEL, value: AGENT_EMAIL },
  ...organizationStore.users.map(user => ({
    label: user.full_name || user.email,
    value: user.email,
  })),
]);

// Reload the form whenever a different review is shown.
watch(
  () => props.review,
  review => {
    description.value = review?.description ?? '';
    assignee.value = review?.assignee_user_email ?? null;
    reporter.value = review?.reporter_user_email ?? null;
    stateUid.value = review?.kanban_state?.uid ?? null;
    dueDate.value = review?.due_date ? new Date(review.due_date).getTime() : null;
    savingError.value = null;
    void fetchAttachedFiles();
  },
  { immediate: true }
);

async function fetchAttachedFiles(): Promise<void> {
  const review = props.review;
  if (!review) {
    attachedFiles.value = [];
    return;
  }

  try {
    const response = await searchFileReview({
      path: { review_uid: review.uid },
      body: { text: '' },
    });
    attachedFiles.value = (response.data?.results ?? []).map(fileReview => ({
      file: fileReview.static_vault_file_copy,
      revision: fileReview.vault_file_revision ?? null,
    }));
  } catch (error) {
    // Without the toast an empty list reads as "no files attached".
    message.error('Could not load the attached files.');
    logger.error('Failed to fetch attached files:', error);
    attachedFiles.value = [];
  }
}

async function save(): Promise<void> {
  const review = props.review;
  if (!review) return;
  if (!canEdit.value) {
    message.error('You do not have permission to update reviews.');
    return;
  }

  isSaving.value = true;
  savingError.value = null;

  try {
    const payload: ReviewUpdateUser = {
      name: review.name,
      description: description.value,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      kanban_column_sorting_position: review.kanban_column_sorting_position || null,
      assignee_user_email: assignee.value,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      created_by_user_email: review.created_by_user_email || null,
      reporter_user_email: reporter.value,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
      kanban_state_uid: stateUid.value || review.kanban_state?.uid || '',
      due_date: dueDate.value ? new Date(dueDate.value).toISOString() : null,
    };

    const response = await updateReview({
      path: { review_uid: review.uid },
      body: payload,
    });
    if (!response.data) throw new Error('Failed to update review');

    message.success('Review updated');
    emit('saved', response.data);
  } catch (error) {
    logger.error('Failed to save review:', error);
    savingError.value = extractErrorDetail(error, 'Failed to save changes. Please try again.');
  } finally {
    isSaving.value = false;
  }
}

async function addFiles(): Promise<void> {
  const review = props.review;
  if (!review) return;
  if (!canEdit.value) {
    message.error('You do not have permission to add files to reviews.');
    return;
  }
  if (addFileSelectedKeys.value.length === 0) {
    message.info('No files selected for addition.');
    return;
  }

  showAddFileModal.value = false;
  processingAddFile.value = true;

  try {
    const fileUids = addFileSelectedKeys.value
      .filter(key => key.startsWith('file-'))
      .map(key => key.replace('file-', ''));
    const dirUids = addFileSelectedKeys.value
      .filter(key => key.startsWith('dir-'))
      .map(key => key.replace('dir-', ''));
    const gaiaUids = addFileSelectedKeys.value
      .filter(key => key.startsWith('gaia-'))
      .map(key => key.replace('gaia-', ''));

    // GAIA files live outside the vault, so they have to be imported before a
    // file review can point at them.
    const importedFileUids: string[] = [];
    if (gaiaUids.length > 0) {
      message.info(`Importing ${gaiaUids.length} GAIA file(s) to vault...`);
      for (const [index, gaiaFileId] of gaiaUids.entries()) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        const fileName = gaiaStore.getGaiaFileById(gaiaFileId)?.title || `File ${gaiaFileId}`;
        try {
          message.info(`Importing ${fileName} (${index + 1}/${gaiaUids.length})...`);
          const imported = await gaiaStore.importGaiaFileToVault(gaiaFileId);
          importedFileUids.push(imported.uid);
        } catch (error) {
          logger.error(`Failed to import GAIA file ${gaiaFileId}:`, error);
          message.error(
            `Failed to import ${fileName}: ${extractErrorDetail(error, 'Failed to add files to review. Please try again.')}`
          );
          return;
        }
      }
      message.success(`Successfully imported ${importedFileUids.length} GAIA file(s) to vault`);
      await vaultStore.getVaultDirectory();
    }

    const combinedFileUids = [...fileUids, ...importedFileUids];
    const payload: FileReviewCreateUser = {
      vault_file_uids: combinedFileUids,
      vault_directory_uids: dirUids,
      ocr_provider: 'unstructured' as const,
    };

    const response = await createFileReviews({
      path: { review_uid: review.uid },
      body: payload,
    });
    if (!response.data) throw new Error('Failed to queue files for addition.');

    message.success(`${combinedFileUids.length + dirUids.length} file(s) queued for addition.`);
    await fetchAttachedFiles();
    addFileSelectedKeys.value = [];
  } catch (error) {
    logger.error('Error adding files to review:', error);
    message.error(
      `Error adding files: ${extractErrorDetail(error, 'Failed to add files to review. Please try again.')}`
    );
  } finally {
    processingAddFile.value = false;
  }
}
</script>

<template>
  <gaia-drawer
    :show="props.show"
    :title="props.review?.name ?? 'Review details'"
    subtitle="Due date, description, people and attachments"
    :width="620"
    @update:show="emit('update:show', $event)"
  >
    <n-flex v-if="props.review" vertical :size="18" :wrap="false">
      <gaia-banner v-if="savingError" type="error">{{ savingError }}</gaia-banner>

      <gaia-form-field label="Kanban state">
        <gaia-select
          v-model:value="stateUid"
          :options="stateOptions"
          :disabled="!canEdit"
          placeholder="Select state"
        />
      </gaia-form-field>

      <gaia-form-field label="Due date">
        <gaia-date-picker v-model:value="dueDate" :disabled="!canEdit" />
      </gaia-form-field>

      <gaia-form-field label="Assignee">
        <gaia-select
          v-model:value="assignee"
          :options="userOptions"
          :disabled="!canEdit"
          clearable
          placeholder="Unassigned"
        />
      </gaia-form-field>

      <gaia-form-field label="Reporter">
        <gaia-select
          v-model:value="reporter"
          :options="userOptions"
          :disabled="!canEdit"
          clearable
          placeholder="No reporter"
        />
      </gaia-form-field>

      <gaia-form-field label="Description">
        <ck-editor-input
          v-model="description"
          extended-mode
          :auto-focus="false"
          :read-only-mode="!canEdit"
          :initial-styling="description ? '' : 'paragraph'"
          placeholder="Enter description…"
        />
      </gaia-form-field>

      <n-flex vertical :size="8" :wrap="false">
        <n-flex align="center" justify="space-between" :size="8" :wrap="false">
          <gaia-section-header :count="String(attachedFiles.length)">Files</gaia-section-header>
          <gaia-add-button
            v-if="canEdit"
            subject="files"
            size="xs"
            :icon="false"
            :loading="processingAddFile"
            @click="showAddFileModal = true"
          />
        </n-flex>
        <gaia-list-item
          v-for="attached in attachedFiles"
          :key="attached.file.uid"
          :clickable="false"
        >
          <gaia-text size="meta" truncate>{{ attached.file.file_name }}</gaia-text>
        </gaia-list-item>
        <gaia-text v-if="attachedFiles.length === 0" size="meta" tone="muted">
          No files attached yet.
        </gaia-text>
      </n-flex>

      <n-flex vertical :size="8" :wrap="false">
        <gaia-section-header>Comments</gaia-section-header>
        <review-comment-section :review-uid="props.review.uid" />
      </n-flex>
    </n-flex>

    <template #footer>
      <gaia-button @click="emit('update:show', false)">Close</gaia-button>
      <gaia-button type="primary" :disabled="!canEdit" :loading="isSaving" @click="save">
        Save changes
      </gaia-button>
    </template>
  </gaia-drawer>

  <gaia-modal
    v-model:show="showAddFileModal"
    title="Add files to review"
    width="min(90vw, 1000px)"
    height="85vh"
    @update:show="showAddFileModal = $event"
  >
    <file-selector v-model:selected-keys="addFileSelectedKeys" />
    <template #action>
      <gaia-button @click="showAddFileModal = false">Cancel</gaia-button>
      <gaia-add-button
        subject="files"
        variant="primary"
        size="md"
        :icon="false"
        :loading="processingAddFile"
        @click="addFiles"
      />
    </template>
  </gaia-modal>
</template>
